import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { logout } from "../redux/authSlice";
import {
  ArrowRight, Menu, X, LogOut, ChevronDown,
  ShieldCheck, Zap, CreditCard,
  Package, MapPin, BarChart2, MessageCircle, Truck,
} from "lucide-react";
import appImg           from "../assets/howto/explore.png";
import logoImg          from "../assets/app/logo.png";
import exploreImg       from "../assets/howto/explore.png";
import listproductImg   from "../assets/howto/listproduct.png";
import manageproductImg from "../assets/howto/manageproduct.png";
import productImg from "../assets/ads/tv.png";
import ordersImg        from "../assets/howto/orders.png";
import registerImg      from "../assets/howto/register.png";
import AppleLogo        from "../assets/app/apple.svg";
import GoogleLogo       from "../assets/app/play.svg";
import SmartFooter      from "./SmartFooter";

/* ─────────────────────────── palette ─────────────────────────── */
const C = {
  primary:  "#6A00FF",
  blue:     "#007BFF",
  pink:     "#FF00D4",
  dark:     "#07050f",
  white:    "#ffffff",
  offwhite: "#f5f4fa",
  muted:    "#6b6880",
  border:   "#e4e2f0",
};

/* ─────────────────────────── global CSS ──────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; color: #07050f; }

  @keyframes gradSpin {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes floatPhone {
    0%, 100% { transform: translateY(0px) rotate(-2deg); }
    50%       { transform: translateY(-18px) rotate(-2deg); }
  }
  @keyframes floatApp {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-12px); }
  }
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.6; transform: scale(1.4); }
  }
  @keyframes gradMove {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes orb1 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(40px, -30px) scale(1.1); }
    66%       { transform: translate(-20px, 20px) scale(0.95); }
  }
  @keyframes orb2 {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33%       { transform: translate(-30px, 20px) scale(1.05); }
    66%       { transform: translate(20px, -30px) scale(1.1); }
  }

  /* ── ANNOUNCE BAR ── */
  .announce-bar {
    position: sticky; top: 0; z-index: 1002; width: 100%;
    background: linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%);
    background-size: 200% 100%; animation: gradMove 4s ease infinite;
    display: flex; align-items: center; justify-content: center;
    padding: 9px 24px; gap: 8px;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #fff;
  }

  /* ── FLOATING NAVBAR ── */
  .navbar {
    position: relative; width: 100%; height: 64px;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 22px;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-radius: 20px;
    box-shadow: 0 4px 32px rgba(106,0,255,0.10), 0 1px 0 rgba(255,255,255,0.8) inset;
  }
  .navbar::before {
    content: ''; position: absolute; inset: -2px; border-radius: 22px;
    background: linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF);
    background-size: 300% 100%; animation: gradSpin 4s linear infinite; z-index: -1;
  }
  .navbar::after {
    content: ''; position: absolute; inset: 2px; border-radius: 18px;
    background: rgba(255,255,255,0.96); backdrop-filter: blur(20px); z-index: -1;
  }

  .nav-links { display: flex; align-items: center; gap: 2px; }
  .nav-link-item {
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 600; color: #333;
    background: none; border: none; padding: 8px 14px; border-radius: 10px;
    cursor: pointer; transition: background 0.15s, color 0.15s; text-decoration: none;
    position: relative; z-index: 1;
  }
  .nav-link-item:hover { background: #f5f4fa; color: #6A00FF; }

  .btn-host {
    position: relative; display: inline-flex; align-items: center; gap: 8px;
    color: #fff; border: none; border-radius: 40px; padding: 13px 26px;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 800;
    cursor: pointer; transition: transform 0.15s, box-shadow 0.2s;
    white-space: nowrap; overflow: hidden; z-index: 0;
    background: linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4);
    background-size: 200% 100%; animation: gradMove 3s ease infinite;
  }
  .btn-host::before {
    content: ''; position: absolute; inset: -2px; border-radius: 42px;
    background: linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4);
    background-size: 200% 100%; animation: gradMove 3s ease infinite; z-index: -1;
  }
  .btn-host:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(106,0,255,0.45); }

  .btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4);
    background-size: 200% 100%; animation: gradMove 3s ease infinite;
    color: #fff; border: none; border-radius: 14px; padding: 14px 28px;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 800;
    cursor: pointer; transition: transform 0.15s, box-shadow 0.2s;
    text-decoration: none; white-space: nowrap;
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(106,0,255,0.4); }

  .btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.14); backdrop-filter: blur(8px);
    color: #fff; border: 1.5px solid rgba(255,255,255,0.45); border-radius: 14px;
    padding: 13px 26px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700;
    cursor: pointer; transition: background 0.2s, transform 0.15s; white-space: nowrap;
  }
  .btn-ghost:hover { background: rgba(255,255,255,0.25); transform: translateY(-2px); }

  .btn-outline {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: #6A00FF; border: 2px solid #6A00FF; border-radius: 14px;
    padding: 12px 24px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700;
    cursor: pointer; transition: background 0.2s, color 0.2s, transform 0.15s; white-space: nowrap;
  }
  .btn-outline:hover { background: #6A00FF; color: #fff; transform: translateY(-2px); }

  .store-hero {
    display: inline-flex; align-items: center; gap: 10px;
    background: #fff; color: #07050f; border: none; border-radius: 14px; padding: 12px 22px;
    cursor: pointer; text-decoration: none;
    box-shadow: 0 2px 16px rgba(106,0,255,0.15); transition: transform 0.2s, box-shadow 0.2s;
  }
  .store-hero:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(106,0,255,0.25); }

  .store-dark {
    display: inline-flex; align-items: center; gap: 12px;
    background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.25);
    border-radius: 14px; padding: 13px 20px;
    cursor: pointer; text-decoration: none; transition: transform 0.2s, box-shadow 0.2s; backdrop-filter: blur(8px);
  }
  .store-dark:hover { transform: translateY(-2px); background: rgba(255,255,255,0.2); }

  .faq-card {
    background: #fff; border: 1.5px solid #e4e2f0; border-radius: 18px;
    margin-bottom: 12px; overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s;
  }
  .faq-card.open { border-color: #6A00FF; box-shadow: 0 0 0 3px rgba(106,0,255,0.08); }
  .faq-q {
    display: flex; align-items: center; justify-content: space-between;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 700; color: #07050f;
    background: none; border: none; width: 100%; text-align: left; cursor: pointer; padding: 20px 24px;
  }
  .faq-a { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #6b6880; line-height: 1.75; padding: 0 24px 20px; }

  .check-item { display: flex; align-items: center; gap: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 15px; font-weight: 600; color: #07050f; }
  .check-dot {
    width: 22px; height: 22px; border-radius: 50%;
    background: linear-gradient(135deg, #007BFF, #FF00D4);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .feat-pill {
    display: flex; align-items: center; gap: 10px;
    background: #fff; border: 1.5px solid #e4e2f0; border-radius: 14px; padding: 14px 18px;
    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #07050f;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s; cursor: default;
  }
  .feat-pill:hover { transform: translateY(-3px); box-shadow: 0 6px 20px rgba(106,0,255,0.12); border-color: #6A00FF; }

  .step-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 24px; padding: 20px; transition: transform 0.3s, box-shadow 0.3s;
  }
  .step-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(0,0,0,0.2); }

  .grad-text {
    background: linear-gradient(90deg, #007BFF 0%, #6A00FF 50%, #FF00D4 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }

  .mobile-menu-overlay {
    position: fixed !important; inset: 0 !important;
    background: #ffffff !important; z-index: 9999 !important;
    display: flex !important; flex-direction: column !important; padding: 24px !important;
  }

  @media (max-width: 900px) {
    .desktop-nav    { display: none !important; }
    .hero-inner     { flex-direction: column !important; text-align: center !important; align-items: center !important; padding-top: 56px !important; padding-bottom: 60px !important; }
    .hero-text      { max-width: 100% !important; align-items: center !important; }
    .hero-phone     { width: 220px !important; margin-top: 32px !important; animation: none !important; transform: none !important; }
    .hero-title     { font-size: 38px !important; }
    .hero-store-row { justify-content: center !important; }
    .steps-grid     { grid-template-columns: 1fr !important; }
    .split-row      { flex-direction: column !important; }
    .split-img      { order: -1 !important; min-height: 240px !important; }
    .split-text     { padding: 28px 24px !important; }
    .stats-row      { grid-template-columns: 1fr 1fr !important; }
    .feat-grid      { grid-template-columns: 1fr 1fr !important; }
    .mc-grid        { grid-template-columns: 1fr !important; }
  }
  @media (min-width: 901px) { .hamburger { display: none !important; } }
`;

/* ─── Gradient Ring ─── */
const GradRing: React.FC<{ children: React.ReactNode; r?: number; pad?: number }> = ({ children, r = 999, pad = 3 }) => (
  <div style={{ position: "relative", display: "inline-flex", borderRadius: r }}>
    <div style={{ position: "absolute", inset: -pad, borderRadius: r + pad, background: "linear-gradient(90deg, #007BFF, #6A00FF, #FF00D4, #6A00FF, #007BFF)", backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", zIndex: 0 }} />
    <div style={{ position: "relative", zIndex: 1, background: "#fff", borderRadius: r + pad - 3, padding: 3, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  </div>
);

/* ─── FAQ Card ─── */
const FAQCard: React.FC<{ q: string; a: string }> = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-card${open ? " open" : ""}`}>
      <button className="faq-q" onClick={() => setOpen(!open)}>
        {q}
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ flexShrink: 0, marginLeft: 12 }}>
          <ChevronDown size={18} color={open ? C.primary : C.muted} />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div className="faq-a" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}>{a}</motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── Check ─── */
const Check: React.FC<{ label: string }> = ({ label }) => (
  <div className="check-item">
    <div className="check-dot">
      <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </div>
    {label}
  </div>
);

/* ══════════════ MAIN ══════════════ */
const MetarelmHome: React.FC = () => {
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();
  const user       = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const isLoggedIn = authLoaded && !!user?.userId;

  const ANNOUNCE_H = 38;
  const [announceVisible, setAnnounceVisible] = useState(true);
  const [scrolled, setScrolled]               = useState(false);
  const [menuOpen, setMenuOpen]               = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setAnnounceVisible(window.scrollY < ANNOUNCE_H);
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onSell   = () => isLoggedIn ? navigate(`/commerceonboarding/${user!.userId}`) : navigate("/authswitch");
  const onLogout = async () => { await dispatch(logout()); navigate("/authswitch"); };

  const go = (id: string) => {
    setMenuOpen(false);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  /* ── Step data ── */
  const sellerSteps = [
    { num: "1", img: registerImg,       title: "Create your account.",  desc: "Sign up in minutes. Verify your identity and set up your seller profile to get started." },
    { num: "2", img: listproductImg,    title: "List your products.",    desc: "Add photos, set your price, and describe your electronics. Fast and easy listing tools." },
    { num: "3", img: manageproductImg,  title: "Manage your inventory.", desc: "Use smart tools to update stock, pricing, and respond to buyers instantly." },
  ];

  const buyerSteps = [
    { num: "1", img: registerImg,  title: "Create your account.", desc: "Sign up in minutes. Verify your identity and you're ready to shop." },
    { num: "2", img: exploreImg,   title: "Explore products.",    desc: "Browse thousands of verified electronics from sellers across 100+ countries." },
    { num: "3", img: ordersImg,    title: "Order & track.",       desc: "Place your order, get instant confirmation, and track delivery in real time." },
  ];

  const features = [
    { icon: <ShieldCheck   size={18}/>, label: "Verified Sellers",   bg: "#f0f4ff", ic: "#007BFF" },
    { icon: <CreditCard    size={18}/>, label: "Instant Payouts",    bg: "#f5f0ff", ic: "#6A00FF" },
    { icon: <MessageCircle size={18}/>, label: "Live Buyer Chat",    bg: "#fff0fc", ic: "#FF00D4" },
    { icon: <BarChart2     size={18}/>, label: "Sales Analytics",    bg: "#f0f8ff", ic: "#007BFF" },
    { icon: <MapPin        size={18}/>, label: "100+ Countries",     bg: "#f8f0ff", ic: "#6A00FF" },
    { icon: <Zap           size={18}/>, label: "Fast Checkout",      bg: "#fff0fa", ic: "#FF00D4" },
    { icon: <Package       size={18}/>, label: "Product Management", bg: "#f0f4ff", ic: "#007BFF" },
    { icon: <Truck         size={18}/>, label: "Order Tracking",     bg: "#f5f0ff", ic: "#6A00FF" },
  ];

  const faqs = [
    { q: "What is Metarelm?",               a: "Metarelm is a global electronics marketplace connecting verified sellers with buyers across 100+ countries. List your products, manage orders, and get paid — all in one place." },
    { q: "How do I become a seller?",         a: "Sign up, verify your identity, and list your products. Onboarding takes less than 10 minutes and you can start receiving orders immediately." },
    { q: "When do I get paid?",               a: "Payouts are processed within 24 hours of order confirmation and deposited directly to your bank account." },
    { q: "Is it free to list?",               a: "Listing on Metarelm is completely free. We only charge a small service fee when a sale is confirmed — no upfront costs, no hidden fees." },
    { q: "How do I become a seller partner?", a: "Click 'Apply as a Partner' in the Partner section. Our team will review and onboard you within 48 hours." },
    { q: "Is my payment secure?",             a: "Yes. All transactions are processed through PCI-DSS compliant payment gateways with bank-grade encryption." },
  ];

  /* ── Steps section ── */
  const StepsSection: React.FC<{ title: string; sub: string; steps: typeof sellerSteps; label: string }> = ({ title, sub, steps, label }) => (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 60 }}>
        <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.3em", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 10 }}>
          <span className="grad-text">{label}</span>
        </p>
        <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 34, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: 10 }}>{title}</h2>
        <p style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", maxWidth: 420, margin: "0 auto" }}>{sub}</p>
      </motion.div>
      <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 28 }}>
        {steps.map((s, i) => (
          <motion.div key={i} className="step-card" initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.13 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#007BFF,#FF00D4)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 20 }}>{s.num}</div>
            <div style={{ borderRadius: 16, overflow: "hidden", marginBottom: 20, background: "rgba(255,255,255,0.04)" }}>
              <img src={s.img} alt={s.title} style={{ width: "100%", display: "block", borderRadius: 14 }} />
            </div>
            <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 8 }}>{s.title}</h3>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.75 }}>{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const navbarTop = announceVisible ? ANNOUNCE_H + 14 : 10;

  return (
    <div style={{ minHeight: "100vh", background: C.white, fontFamily: "'Plus Jakarta Sans',sans-serif", color: C.dark, overflowX: "hidden" }}>
      <style>{CSS}</style>

      {/* ── Announce bar ── */}
      <div className="announce-bar">
        <span>⚡</span>
        <span>🎉 Metarelm Electronics Marketplace is now live! &nbsp;
          <strong style={{ color: "#fff", cursor: "pointer", textDecoration: "underline" }} onClick={onSell}>Start selling today →</strong>
        </span>
      </div>

      {/* ── Floating navbar ── */}
      <div style={{ position: "fixed", top: navbarTop, left: 20, right: 20, margin: "0 auto", maxWidth: 1080, zIndex: 1000, transition: "top 0.3s ease" }}>
        <nav className={`navbar${scrolled ? " scrolled" : ""}`}>
          <div onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            <GradRing r={12} pad={2}>
              <img src={logoImg} alt="Metarelm" style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", display: "block" }} />
            </GradRing>
            <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 16, color: C.dark }}>Metarelm</span>
          </div>

          <div className="nav-links desktop-nav" style={{ position: "relative", zIndex: 1 }}>
            <button className="nav-link-item" onClick={() => go("how-sell")}>How it works</button>
            <button className="nav-link-item" onClick={() => go("partner")}>For Partners</button>
            <button className="nav-link-item" onClick={() => go("faq")}>FAQs</button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative", zIndex: 1 }}>
            {!isLoggedIn ? (
              <>
                <button className="nav-link-item desktop-nav" onClick={() => navigate("/authswitch")} style={{ color: C.muted }}>Sign in</button>
                <button className="btn-host" onClick={onSell}>Become a Seller <ArrowRight size={13} /></button>
              </>
            ) : (
              <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, color: C.muted, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                <LogOut size={13} /> Logout
              </button>
            )}
            <button className="hamburger" onClick={() => setMenuOpen(true)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
              <Menu size={22} color={C.dark} />
            </button>
          </div>
        </nav>
      </div>

      {/* ── Mobile menu ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div className="mobile-menu-overlay" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.25 }}
            style={{ position: "fixed", inset: 0, background: "#ffffff", zIndex: 9999, display: "flex", flexDirection: "column", padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40 }}>
              <GradRing r={12} pad={2}>
                <img src={logoImg} alt="Metarelm" style={{ width: 34, height: 34, borderRadius: 10, objectFit: "cover", display: "block" }} />
              </GradRing>
              <button onClick={() => setMenuOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={24} color={C.dark} />
              </button>
            </div>
            {[["How it works","how-sell"],["For Partners","partner"],["FAQs","faq"]].map(([l, id], i) => (
              <button key={i} onClick={() => go(id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 0", background: "none", border: "none", borderBottom: `1px solid ${C.border}`, fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 18, fontWeight: 700, color: C.dark, cursor: "pointer", width: "100%" }}>
                {l} <ArrowRight size={18} color={C.primary} />
              </button>
            ))}
            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
              {!isLoggedIn && (
                <button className="btn-outline" style={{ width: "100%", justifyContent: "center", padding: "15px" }} onClick={() => { setMenuOpen(false); navigate("/authswitch"); }}>Sign in</button>
              )}
              <button className="btn-primary" style={{ width: "100%", justifyContent: "center", padding: "15px", borderRadius: 14, fontSize: 15 }} onClick={() => { setMenuOpen(false); onSell(); }}>
                Become a Seller <ArrowRight size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Spacer ── */}
      <div style={{ height: ANNOUNCE_H + 14 + 64 + 20 }} />

      {/* ══ HERO ══ */}
      <section style={{ position: "relative", background: "#07050f", minHeight: "calc(100vh - 116px)", overflow: "hidden", marginTop: -20 }}>
        <div style={{ position: "absolute", top: "10%", left: "5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,123,255,0.25) 0%, transparent 65%)", animation: "orb1 8s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "20%", right: "8%", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,212,0.2) 0%, transparent 65%)", animation: "orb2 10s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "15%", left: "35%", width: 340, height: 340, borderRadius: "50%", background: "radial-gradient(circle, rgba(106,0,255,0.18) 0%, transparent 65%)", animation: "orb1 12s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />

        <div className="hero-inner" style={{ position: "relative", zIndex: 2, maxWidth: 1080, margin: "0 auto", padding: "100px 48px 100px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 48 }}>
          <motion.div className="hero-text" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            style={{ flex: 1, maxWidth: 560, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.07)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 100, padding: "7px 16px", marginBottom: 28, alignSelf: "flex-start" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "linear-gradient(135deg,#007BFF,#FF00D4)", animation: "pulse-dot 2s ease-in-out infinite" }} />
              <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 13, color: "rgba(255,255,255,0.88)", fontWeight: 600 }}>Global electronics marketplace · 100+ countries</span>
            </div>
            <h1 className="hero-title" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 62, fontWeight: 900, color: "#fff", lineHeight: 1.04, letterSpacing: "-0.04em", marginBottom: 18 }}>
              The future of<br/>
              <span className="grad-text">electronics.</span>
            </h1>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,0.7)", lineHeight: 1.7, marginBottom: 8, fontWeight: 500 }}>Buy &amp; sell electronics on Metarelm.</p>
            <p style={{ fontSize: 15, color: "rgba(255,255,255,0.4)", lineHeight: 1.7, marginBottom: 38, maxWidth: 420 }}>Fast, transparent, and built for the next generation of commerce.</p>
            <div className="hero-store-row" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 32 }}>
              <a href="https://apps.apple.com/app/idYOUR_APP_ID" target="_blank" rel="noopener noreferrer" className="store-hero">
                <img src={AppleLogo} alt="App Store" style={{ height: 22 }} />
                <div>
                  <div style={{ fontSize: 9, color: "#888", letterSpacing: "0.08em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>GET IT ON THE</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.dark, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>App Store</div>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.metarelm.android" target="_blank" rel="noopener noreferrer" className="store-hero">
                <img src={GoogleLogo} alt="Google Play" style={{ height: 22 }} />
                <div>
                  <div style={{ fontSize: 9, color: "#888", letterSpacing: "0.08em", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>GET IT ON</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.dark, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Google Play</div>
                </div>
              </a>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button className="btn-primary" style={{ fontSize: 14, padding: "14px 28px" }} onClick={onSell}>Become a Seller <ArrowRight size={14} /></button>
              {!isLoggedIn && <button className="btn-ghost" style={{ fontSize: 14, padding: "14px 28px" }} onClick={() => navigate("/authswitch")}>Sign In <ArrowRight size={14} /></button>}
            </div>
          </motion.div>

          <motion.div className="hero-phone" initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.22 }} style={{ flexShrink: 0, width: 290 }}>
            <div style={{ position: "relative", display: "inline-block", animation: "floatPhone 5s ease-in-out infinite" }}>
              <div style={{ position: "absolute", inset: -6, borderRadius: 46, background: "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF)", backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", zIndex: 0 }} />
              <div style={{ position: "relative", zIndex: 1, background: "rgba(7,5,15,0.6)", borderRadius: 42, padding: 4 }}>
                <img src={appImg} alt="Metarelm App" style={{ width: 282, borderRadius: 38, display: "block", filter: "drop-shadow(0 20px 40px rgba(106,0,255,0.4))" }} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 52, background: C.white, clipPath: "polygon(0 100%,3% 30%,6% 100%,9% 30%,12% 100%,15% 30%,18% 100%,21% 30%,24% 100%,27% 30%,30% 100%,33% 30%,36% 100%,39% 30%,42% 100%,45% 30%,48% 100%,51% 30%,54% 100%,57% 30%,60% 100%,63% 30%,66% 100%,69% 30%,72% 100%,75% 30%,78% 100%,81% 30%,84% 100%,87% 30%,90% 100%,93% 30%,96% 100%,100% 30%,100% 100%)", pointerEvents: "none" }} />
      </section>

      {/* ── Stats ── */}
      <div className="stats-row" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: `1px solid ${C.border}`, background: C.white }}>
        {[{ v: "100+", l: "Countries" }, { v: "Instant", l: "Payouts" }, { v: "24/7", l: "Support" }, { v: "Verified", l: "Sellers Only" }].map((s, i) => (
          <div key={i} style={{ textAlign: "center", padding: "22px 16px", borderRight: i < 3 ? `1px solid ${C.border}` : "none" }}>
            <div className="grad-text" style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 22, fontWeight: 900 }}>{s.v}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, letterSpacing: "0.08em", textTransform: "uppercase" as const }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* ══ METACOMMERCE — logged-in ══ */}
      {isLoggedIn && (
        <section style={{ padding: "72px 24px 40px", background: C.white }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.3em", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 6 }}>
                    <span className="grad-text">METACOMMERCE</span>
                  </p>
                  <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 28, fontWeight: 800, color: C.dark, letterSpacing: "-0.02em" }}>Explore Products</h2>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button className="btn-primary" style={{ fontSize: 12, padding: "10px 22px" }} onClick={() => navigate("/metacommerce")}>
                    Browse All <ArrowRight size={13} />
                  </button>
                  <button
                    onClick={() => navigate(`/orders/${user!.userId}`)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1.5px solid ${C.border}`, borderRadius: 12, padding: "10px 20px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.dark, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    <Package size={13} /> Manage Orders
                  </button>
                </div>
              </div>

              {/* 3-card grid */}
              <div className="mc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
                {[
                  { img: productImg,     label: "All Electronics",  route: "/producthome" },
                  { img: listproductImg, label: "Sell a Product",   route: `/commerceonboarding/${user!.userId}` },
                  { img: ordersImg,      label: "Manage Orders",    route: `/orders/${user!.userId}` },
                ].map((card, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    onClick={() => navigate(card.route)}
                    style={{ position: "relative", cursor: "pointer", borderRadius: 20, overflow: "hidden", height: 200 }}
                    whileHover={{ y: -4 } as any}>
                    <img src={card.img} alt={card.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(7,5,15,0.78) 0%, transparent 58%)", display: "flex", alignItems: "flex-end", padding: "20px" }}>
                      <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 16, fontWeight: 800, color: "#fff" }}>{card.label}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ══ FEATURES ══ */}
      <section style={{ padding: "80px 24px", background: C.white }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 34, fontWeight: 900, color: C.dark, letterSpacing: "-0.02em", marginBottom: 10 }}>Everything you need to sell</h2>
            <p style={{ fontSize: 15, color: C.muted }}>From listing to payout — Metarealm handles it all.</p>
          </motion.div>
          <div className="feat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
            {features.map((f, i) => (
              <motion.div key={i} className="feat-pill" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} style={{ background: f.bg }}>
                <span style={{ color: f.ic, flexShrink: 0 }}>{f.icon}</span>{f.label}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS — SELLERS ══ */}
      <section id="how-sell" style={{ background: "#07050f", padding: "90px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "30%", left: "-10%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,123,255,0.1) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "20%", right: "-5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,212,0.1) 0%, transparent 65%)", pointerEvents: "none" }} />
        <StepsSection title="How to sell on Metarelm" sub="Start earning from your electronics today." steps={sellerSteps} label="FOR SELLERS" />
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginTop: 52 }}>
          <button className="btn-primary" style={{ fontSize: 15, padding: "16px 36px" }} onClick={onSell}>Become a Seller <ArrowRight size={15} /></button>
        </motion.div>
      </section>

      {/* ══ HOW IT WORKS — BUYERS ══ 
      
            <section style={{ background: "#0c0a18", padding: "90px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "20%", right: "-8%", width: 450, height: 450, borderRadius: "50%", background: "radial-gradient(circle, rgba(106,0,255,0.12) 0%, transparent 65%)", pointerEvents: "none" }} />
        <StepsSection title="How to shop on Metarelm" sub="Find the best electronics and order in seconds." steps={buyerSteps} label="FOR BUYERS" />
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginTop: 52 }}>
          <button className="btn-primary" style={{ fontSize: 15, padding: "16px 36px" }} onClick={() => navigate("/authswitch")}>Start Shopping <ArrowRight size={15} /></button>
        </motion.div>
      </section>
      */}


      {/* ══ PARTNER ══ */}
      <section id="partner" style={{ background: C.offwhite, padding: "90px 24px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div className="split-row" style={{ display: "flex", alignItems: "stretch", background: "#fff", borderRadius: 28, overflow: "hidden", boxShadow: "0 4px 40px rgba(106,0,255,0.08)" }}>
            <div className="split-img" style={{ flex: "0 0 44%", position: "relative", minHeight: 380 }}>
              <img src={manageproductImg} alt="Become a Seller" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, transparent 50%, #fff 100%)" }} />
            </div>
            <motion.div className="split-text" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ flex: 1, padding: "48px 40px" }}>
              <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.3em", fontFamily: "'Plus Jakarta Sans',sans-serif", marginBottom: 12 }}>
                <span className="grad-text">PARTNER</span>
              </p>
              <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 30, fontWeight: 900, color: C.dark, lineHeight: 1.2, marginBottom: 14, letterSpacing: "-0.02em" }}>Become a Seller</h2>
              <p style={{ fontSize: 15, color: C.muted, lineHeight: 1.7, marginBottom: 28 }}>Sell on your schedule. Get paid fast. Join a verified community of sellers that respects your time and products.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 }}>
                <Check label="No upfront costs" />
                <Check label="Instant payouts after sale" />
                <Check label="Flexible inventory management" />
                <Check label="Verified buyers only" />
                <Check label="24/7 dedicated seller support" />
              </div>
              <button className="btn-primary" style={{ fontSize: 14, padding: "13px 28px" }} onClick={onSell}>Apply as a Seller <ArrowRight size={14} /></button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ APP DOWNLOAD ══ */}
      <section style={{ background: "linear-gradient(135deg, #07050f 0%, #0d0620 50%, #07050f 100%)", padding: "80px 24px", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", top: -80, right: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(106,0,255,0.15) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, left: -40, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,123,255,0.12) 0%, transparent 65%)", pointerEvents: "none" }} />
        <div className="split-row" style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", gap: 56, position: "relative" }}>
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} style={{ flex: 1 }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 34, fontWeight: 900, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 14 }}>
              Experience <span className="grad-text">Metarelm!</span>
            </h2>
            <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", lineHeight: 1.75, marginBottom: 32 }}>List products, manage orders, and track your sales from anywhere. Available on iOS and Android.</p>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a href="https://apps.apple.com/app/idYOUR_APP_ID" target="_blank" rel="noopener noreferrer" className="store-dark">
                <img src={AppleLogo} alt="App Store" style={{ height: 20, filter: "brightness(0) invert(1)", position: "relative", zIndex: 1 }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: "0.1em" }}>DOWNLOAD ON THE</div>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>App Store</div>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.metarelm.android" target="_blank" rel="noopener noreferrer" className="store-dark">
                <img src={GoogleLogo} alt="Google Play" style={{ height: 20, position: "relative", zIndex: 1 }} />
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: "0.1em" }}>GET IT ON</div>
                  <div style={{ fontSize: 13, color: "#fff", fontWeight: 800, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Google Play</div>
                </div>
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }} style={{ flexShrink: 0, animation: "floatApp 4s ease-in-out infinite" }}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <div style={{ position: "absolute", inset: -5, borderRadius: 30, background: "linear-gradient(90deg,#007BFF,#6A00FF,#FF00D4,#6A00FF,#007BFF)", backgroundSize: "300% 100%", animation: "gradSpin 3s linear infinite", zIndex: 0 }} />
              <img src={ordersImg} alt="Metarelm App" style={{ position: "relative", zIndex: 1, width: 240, borderRadius: 26, display: "block", objectFit: "cover" }} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section id="faq" style={{ background: C.white, padding: "90px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 56 }}>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 38, fontWeight: 900, color: C.dark, letterSpacing: "-0.02em" }}>FAQs</h2>
            <p style={{ fontSize: 15, color: C.muted, marginTop: 10 }}>Everything you need to know about selling on Metarelm.</p>
          </motion.div>
          {[
            { group: "About Metarelm",     items: faqs.slice(0, 2) },
            { group: "Selling & Earning",  items: faqs.slice(2, 4) },
            { group: "Partners & Payment", items: faqs.slice(4) },
          ].map((g, gi) => (
            <div key={gi} style={{ marginBottom: 40 }}>
              <h3 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 16, fontWeight: 800, color: C.dark, marginBottom: 16 }}>{g.group}</h3>
              {g.items.map((f, i) => <FAQCard key={i} q={f.q} a={f.a} />)}
            </div>
          ))}
        </div>
      </section>

      {/* ══ BOTTOM CTA ══ */}
      <section style={{ background: "#07050f", padding: "90px 24px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle, rgba(106,0,255,0.15) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "50%", left: "30%", transform: "translate(-50%,-50%)", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(0,123,255,0.1) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "50%", left: "70%", transform: "translate(-50%,-50%)", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,0,212,0.1) 0%, transparent 60%)", pointerEvents: "none" }} />
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ position: "relative" }}>
          <div style={{ display: "inline-block", marginBottom: 28 }}>
            <GradRing r={50} pad={3}>
              <div style={{ background: C.dark, borderRadius: 48, padding: "10px 28px" }}>
                <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 12, fontWeight: 800, color: "#fff", letterSpacing: "0.2em" }}>METARELM</span>
              </div>
            </GradRing>
          </div>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 42, fontWeight: 900, color: "#fff", letterSpacing: "-0.03em", marginBottom: 14, lineHeight: 1.1 }}>
            Ready to start <span className="grad-text">selling?</span>
          </h2>
          <p style={{ fontSize: 16, color: "rgba(255,255,255,0.45)", margin: "0 auto 40px", maxWidth: 400, lineHeight: 1.7 }}>Join thousands of sellers and buyers already on Metarelm across 100+ countries.</p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn-host" style={{ fontSize: 15, padding: "16px 36px" }} onClick={onSell}>Become a Seller <ArrowRight size={15} /></button>
            {!isLoggedIn && (
              <button className="btn-ghost" style={{ fontSize: 15, padding: "15px 30px", borderRadius: 40 }} onClick={() => navigate("/authswitch")}>Sign In</button>
            )}
          </div>
        </motion.div>
      </section>

      <SmartFooter />
    </div>
  );
};

export default MetarelmHome;